import { AvailabilityStatus } from "@photography-booking/shared";
import {
  MONTH_NAMES,
  WEEKDAY_LABELS,
  buildMonthGrid,
  type YearMonth,
} from "../lib/calendar.js";

interface AvailabilityCalendarProps {
  /** The month currently shown. */
  view: YearMonth;
  /** Status per ISO date for the visible month (status-only — Req 6.1, 6.2). */
  statuses: ReadonlyMap<string, AvailabilityStatus>;
  /** Whether availability for this month is still loading. */
  loading: boolean;
  /** A load error message, or null when the fetch succeeded. */
  error: string | null;
  /** The currently selected ISO date, or null when none is chosen. */
  selectedDate: string | null;
  /** Invoked with the ISO date when an available day is chosen. */
  onSelectDate: (iso: string) => void;
  /** Step to the previous month. */
  onPrevMonth: () => void;
  /** Step to the next month. */
  onNextMonth: () => void;
}

/**
 * Status-only availability calendar (Req 6.1, 6.2, 6.7).
 *
 * Renders a month grid where each day reflects exactly one of AVAILABLE /
 * UNAVAILABLE — never a reason, event title, or any personal schedule detail,
 * because the data the calendar receives carries none. Only AVAILABLE days in
 * the focused month are selectable; unavailable, adjacent-month, and
 * not-yet-loaded days are non-interactive. The layout is fluid so it stays
 * usable across macOS/iOS, Windows, and Android browsers (Req 6.7).
 */
export function AvailabilityCalendar({
  view,
  statuses,
  loading,
  error,
  selectedDate,
  onSelectDate,
  onPrevMonth,
  onNextMonth,
}: AvailabilityCalendarProps): JSX.Element {
  const cells = buildMonthGrid(view);
  const heading = `${MONTH_NAMES[view.month]} ${view.year}`;

  return (
    <div className="calendar" role="group" aria-label="Availability calendar">
      <div className="calendar__header">
        <button
          type="button"
          className="calendar__nav"
          aria-label="Previous month"
          onClick={onPrevMonth}
        >
          &#8249;
        </button>
        <h3 className="calendar__title" aria-live="polite">
          {heading}
        </h3>
        <button
          type="button"
          className="calendar__nav"
          aria-label="Next month"
          onClick={onNextMonth}
        >
          &#8250;
        </button>
      </div>

      <div className="calendar__weekdays" aria-hidden="true">
        {WEEKDAY_LABELS.map((label) => (
          <span key={label} className="calendar__weekday">
            {label}
          </span>
        ))}
      </div>

      {error ? (
        <p className="calendar__message calendar__message--error" role="alert">
          {error}
        </p>
      ) : (
        <div
          className={`calendar__grid${loading ? " calendar__grid--loading" : ""}`}
          aria-busy={loading}
        >
          {cells.map((cell) => {
            const status = cell.inMonth ? statuses.get(cell.iso) : undefined;
            const isAvailable =
              cell.inMonth && status === AvailabilityStatus.AVAILABLE;
            const isSelected = cell.iso === selectedDate;

            const stateLabel = !cell.inMonth
              ? undefined
              : status === AvailabilityStatus.AVAILABLE
                ? "Available"
                : status === AvailabilityStatus.UNAVAILABLE
                  ? "Unavailable"
                  : undefined;

            const classes = [
              "calendar__day",
              cell.inMonth ? "" : "calendar__day--muted",
              isAvailable
                ? "calendar__day--available"
                : cell.inMonth && status
                  ? "calendar__day--unavailable"
                  : "",
              isSelected ? "calendar__day--selected" : "",
            ]
              .filter(Boolean)
              .join(" ");

            return (
              <button
                key={cell.iso}
                type="button"
                className={classes}
                disabled={!isAvailable}
                aria-pressed={isSelected}
                aria-label={
                  stateLabel ? `${cell.iso} — ${stateLabel}` : undefined
                }
                onClick={() => onSelectDate(cell.iso)}
              >
                <span className="calendar__day-number">{cell.day}</span>
              </button>
            );
          })}
        </div>
      )}

      <ul className="calendar__legend" aria-hidden="true">
        <li className="calendar__legend-item calendar__legend-item--available">
          Available
        </li>
        <li className="calendar__legend-item calendar__legend-item--unavailable">
          Unavailable
        </li>
      </ul>
    </div>
  );
}
